import { FC, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, If } from 'components';
import { Job } from '../../jobs.model';
import {
  ArchiveJobIcon,
  CaretDownIcon,
  ChevronArrowRightIcon,
  DeleteIcon,
  DuplicateIcon,
  EditIconBlue,
  GreenTickIcon,
  JobsTabIcon,
  LineItemPlusIcon
} from 'assets/icons';
import { getJobStatusText, getStatusBGColor, getStatusColor } from '../../utils';
import routesPath from 'routes/RoutesPath';
import { ButtonType } from 'components/custom-button/CustomButton';
import { CustomPopover } from 'components';
import { PopoverArrowTypes } from 'constants/common';
import MoreMenu from 'containers/receipts/components/MoreMenu';
import { JobStatus } from '../../constant';
import JobTagsManager from './JobTagsManager';
import { useGetTagsQuery } from 'containers/tags/api';
import { useAppSelector } from 'hooks/typedHooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { InfoMesssages } from 'types/infoMessages';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { JobsCleverTapEvents } from 'containers/jobs/events';
import { logAnalyticEvent } from 'utils/analytics';
import { JobPermissionsContext } from '../JobDetails';

interface JobDetailsHeaderProps {
  job: Job;
  handleStatusChange: (status?: JobStatus) => void;
  handleDeleteJob: () => void;
}

const JobDetailsHeader: FC<JobDetailsHeaderProps> = ({ job, handleStatusChange, handleDeleteJob }) => {
  const navigate = useNavigate();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const jobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { data: tagsData, isLoading: isTagsLoading } = useGetTagsQuery({ teamId: currentTeam.id, isActive: true });
  const canModifyDoneJobs = jobPermissions?.modifyDoneJobs;
  const { canEditJobs } = useContext(JobPermissionsContext);

  const disabledReason = () => {
    if (job.status.toUpperCase() === JobStatus.DONE && !canModifyDoneJobs) {
      return InfoMesssages.editDoneJobNoPermissionMessage;
    } else if (!job?.isEditable) {
      return InfoMesssages.cannotEditJobUnderPR;
    }
    return '';
  };
  const toggleIsMoreOpen = () => {
    setIsMoreOpen(!isMoreOpen);
  };

  const closeMoreOption = () => {
    setIsMoreOpen(false);
  };

  const getMoreMenuItems = (status: JobStatus) => {
    const cancelItem = {
      id: 'more-button-cancel-job',
      icon: <JobsTabIcon className="path path-fill-current h-[16px] w-[16px] shrink-0 text-secondary" />,
      text: 'Cancel job',
      onClick: () => {
        handleStatusChange(JobStatus.CANCELED);
      }
    };
    const deleteItem = {
      id: 'more-button-delete-job',
      icon: <DeleteIcon className="h-[18px] w-[18px] shrink-0" />,
      text: 'Delete job',
      onClick: handleDeleteJob
    };

    const archiveItem = {
      id: 'more-button-archive-job',
      icon: <ArchiveJobIcon className="h-[18px] w-[18px] shrink-0" />,
      text: 'Archive job',
      onClick: () => handleStatusChange(JobStatus.ARCHIVED)
    };

    const duplicateItem = {
      id: 'more-button-duplicate-job',
      icon: <DuplicateIcon className="h-[19px] w-[19px] shrink-0" />,
      text: 'Duplicate job',
      onClick: () => {
        logAnalyticEvent(JobsCleverTapEvents.webDuplicateJob, {
          jobId: job.id,
          source: 'job-details-page'
        });
        navigate(routesPath.DUPLICATE_JOB.replace(':jobId', job.id));
      }
    };

    const estimateItem = {
      id: 'more-button-create-estimate',
      icon: <LineItemPlusIcon className="h-[18px] w-[18px] shrink-0" />,
      text: job.estimates ? 'View estimate' : 'Create estimate',
      onClick: () =>
        navigate(
          job.estimates
            ? `/jobs/${currentTeam.id}/${job.id}/estimate/${job.estimates[0].id}`
            : `/estimates/create?jobId=${job.id}`,
          {
            state: ROOT_ROUTE_STATE
          }
        )
    };

    const baseItems = [
      {
        id: 'more-button-create-invoice',
        icon: <LineItemPlusIcon className="h-[18px] w-[18px] shrink-0" />,
        text: job.latestInvoice ? 'View invoice' : 'Create invoice',
        onClick: () =>
          navigate(
            job.latestInvoice
              ? `/invoices/${currentTeam.id}/${job.latestInvoice.id}`
              : `/invoices/create?jobId=${job.id}`,
            {
              state: ROOT_ROUTE_STATE
            }
          )
      },
      ...(job.latestInvoice && !job.estimates ? [] : [estimateItem]),
      duplicateItem
    ];

    const markDoneItem = {
      id: 'more-button-mark-done',
      icon: <GreenTickIcon className="h-[18px] w-[18px] shrink-0" />,
      text: 'Mark done',
      onClick: () => handleStatusChange(JobStatus.DONE)
    };

    const markDonePendingApprovalItem = {
      id: 'more-button-mark-done-pending-approval',
      icon: <GreenTickIcon className="h-[18px] w-[18px] shrink-0" />,
      text: 'Mark done pending approval',
      onClick: () => handleStatusChange(JobStatus.DONE_PENDING_APPROVAL)
    };

    switch (status) {
      case JobStatus.SUBMITTED:
      case JobStatus.CONFIRMED:
      case JobStatus.PENDING:
      case JobStatus.INBOX:
        return [...baseItems, deleteItem, cancelItem, archiveItem];

      case JobStatus.DONE:
        return [...baseItems, ...(canModifyDoneJobs ? [deleteItem, cancelItem, archiveItem] : [])];

      case JobStatus.IN_PROGRESS:
        return [
          ...(canModifyDoneJobs ? [markDoneItem] : [markDonePendingApprovalItem]),
          ...baseItems,
          deleteItem,
          cancelItem,
          archiveItem
        ];
      case JobStatus.DONE_PENDING_APPROVAL:
        return [...(canModifyDoneJobs ? [markDoneItem] : []), ...baseItems, deleteItem, cancelItem, archiveItem];

      case JobStatus.CANCELED:
      case JobStatus.ARCHIVED:
        return [duplicateItem, deleteItem];

      default:
        return [];
    }
  };

  const handleEditJobClick = () => {
    navigate(routesPath.EDIT_JOB.replace(':jobId', job.id));
  };

  const moreMenuItems = getMoreMenuItems(job.status);

  const moreAnchorElem = () => (
    <Button
      id="job-details-actions-button"
      type={ButtonType.SECONDARY}
      className="h-10 border border-secondary px-4 text-secondary"
      onClick={toggleIsMoreOpen}>
      <div className="flex items-center gap-2 text-[17px]">
        Actions
        <CaretDownIcon className="path path-stroke-current h-[8x] w-[8x] shrink-0 text-secondary" />
      </div>
    </Button>
  );

  return (
    <div className="flex items-start justify-between border-b border-borderGray px-6 py-4">
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2 text-xl">
          <div className="flex items-center">
            <span
              className="mr-2 cursor-pointer font-bold text-headingAccent"
              onClick={() => navigate(routesPath.JOBS)}>
              Jobs
            </span>
            <span className="text-accentText">
              <ChevronArrowRightIcon className="path path-fill-current h-[8x] w-[8x] shrink-0 text-gray-1" />
            </span>
          </div>
          <div className="font-bold text-headingGray">
            #{job.jobNumber} - {job.title}
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="flex text-17px font-semibold text-headingAccent">
            <span className="w-[56px]">Status</span>
            <span>:</span>
          </div>
          <div
            className="rounded px-2.5 py-1 font-semibold leading-[18px]"
            style={{ backgroundColor: getStatusBGColor(job.status), color: getStatusColor(job.status) }}>
            {getJobStatusText(job.status)}
          </div>
          <If
            condition={
              [
                JobStatus.SUBMITTED,
                JobStatus.CONFIRMED,
                JobStatus.PENDING,
                JobStatus.IN_PROGRESS,
                JobStatus.CANCELED,
                JobStatus.DONE_PENDING_APPROVAL
              ].includes(job.status) || canEditJobs
            }>
            <EditIconBlue className="h-[18px] w-[18px] shrink-0" onClick={() => handleStatusChange()} />
          </If>
        </div>
        <div className="flex items-start gap-2.5">
          <div className="flex pt-1 text-17px font-semibold text-headingAccent">
            <span className="w-[56px]">Tags</span>
            <span>:</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <JobTagsManager
              tags={tagsData?.data?.tags || []}
              jobTags={job.jobTags || []}
              jobNumber={job.jobNumber}
              isLoadingTags={isTagsLoading}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Button
          showDisabledReason={!canEditJobs}
          disabledReason={disabledReason()}
          type={ButtonType.SECONDARY}
          id="job-details-edit-button"
          className="h-10 border border-secondary px-4 text-secondary"
          onClick={handleEditJobClick}>
          <div className="flex items-center gap-2 text-[17px]">
            Edit
            <EditIconBlue className="h-[18px] w-[18px] shrink-0" />
          </div>
        </Button>
        <div className="relative cursor-pointer">
          <CustomPopover
            show={isMoreOpen}
            anchorComponent={moreAnchorElem}
            onTogglePopover={toggleIsMoreOpen}
            onClosePopover={closeMoreOption}
            offset="-right-2 top-12"
            minWidth="min-w-[168px] mt-2"
            showArrow={true}
            arrowType={PopoverArrowTypes.TOP}
            highlightAnchor={false}>
            <MoreMenu menuItems={moreMenuItems} />
          </CustomPopover>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsHeader;
